import getDistanceFromLatLonInMeters from './getDistanceFromLatLonInMeters';
import {
  LOCAL_PLACE_SUGGESTIONS,
  getPlaceSuggestions,
  trim,
} from './localOpsConciergeIntelligence';

const REGION_BOUNDS = {
  camden: {
    minLon: -88.45,
    minLat: 35.85,
    maxLon: -87.75,
    maxLat: 36.35,
  },
};
const REGION_SEARCH_CONTEXT = {
  camden: {
    city: 'Camden',
    state: 'TN',
    postalCode: '38320',
    label: 'Camden TN',
    proximity: '-88.097,36.058',
  },
};

const boundsToBbox = (bounds) =>
  bounds
    ? `${bounds.minLon},${bounds.minLat},${bounds.maxLon},${bounds.maxLat}`
    : '';

const normalizeWords = (value) =>
  trim(value)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

const BUSINESS_STOP_WORDS = new Set([
  'camden',
  'tn',
  'tennessee',
  'us',
  'usa',
  'united',
  'states',
  'near',
  'the',
  'restaurant',
  'store',
]);

const hasStreetNumber = (value) =>
  /\b\d{1,6}\s+[A-Za-z0-9.'\-\s]{2,}\b/.test(trim(value));

const hasStreetTerm = (value) =>
  /\b(street|st|road|rd|drive|dr|lane|ln|avenue|ave|highway|hwy|boulevard|blvd|circle|court|ct|way)\b/i.test(
    trim(value),
  );

const extractZipCodes = (value) => trim(value).match(/\b\d{5}\b/g) || [];

const getRegionSearchContext = (regionSlug = 'camden') =>
  REGION_SEARCH_CONTEXT[regionSlug] || {};

const getBusinessStopWords = (regionSlug = 'camden') => {
  const context = getRegionSearchContext(regionSlug);
  return new Set([
    ...BUSINESS_STOP_WORDS,
    ...normalizeWords(context.city),
    ...normalizeWords(context.state),
    ...normalizeWords(context.label),
  ]);
};

const queryCoreWords = (value, regionSlug = 'camden') => {
  const stopWords = getBusinessStopWords(regionSlug);
  return normalizeWords(value).filter(
    (word) => !stopWords.has(word) && !/^\d{5}$/.test(word),
  );
};

const roundCoord = (value, decimals = 6) => {
  const factor = 10 ** decimals;
  return Math.round(Number(value) * factor) / factor;
};

export const isAddressLikeInput = (value) => {
  const text = trim(value);
  if (!text) return false;
  return (
    /\b\d{1,6}\s+[A-Za-z0-9.'\-\s]{2,}\b/.test(text) ||
    /\b[A-Za-z.'\-\s]+,\s*[A-Z]{2}\b/.test(text) ||
    /\bCamden,\s*TN\b/i.test(text) ||
    /\bTN\s*\d{5}\b/i.test(text)
  );
};

const placeAddressText = (place) =>
  trim(place.publicAddress || place.addressText || place.placeName || place.name);

const placeCoordinates = (place) => {
  if (!place) return null;
  const coords = place.coordinates || place.center;
  const lat = Number(coords?.lat ?? coords?.latitude);
  const lon = Number(coords?.lon ?? coords?.lng ?? coords?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return { lat: roundCoord(lat), lon: roundCoord(lon) };
};

const coordinatesInRegion = (coords, regionSlug = 'camden') => {
  const bounds = REGION_BOUNDS[regionSlug];
  if (!bounds || !coords) return true;
  const lat = Number(coords.lat);
  const lon = Number(coords.lon);
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    lat >= bounds.minLat &&
    lat <= bounds.maxLat &&
    lon >= bounds.minLon &&
    lon <= bounds.maxLon
  );
};

const normalizeSeededCandidate = (place) => ({
  id: place.id,
  source: 'seeded',
  name: place.name,
  label: place.name,
  addressText: placeAddressText(place),
  addressStatus: place.addressStatus || 'label_only',
  placeKind: place.placeKind,
  privacyLevel: place.privacyLevel,
  coordinates: placeCoordinates(place),
  requiresConfirmation: place.addressStatus !== 'label_only',
  canQuote: Boolean(placeCoordinates(place)),
});

const normalizeMapboxCandidate = (feature) => {
  const center = feature?.center;
  if (!Array.isArray(center) || center.length < 2) return null;
  const [lon, lat] = center;
  if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lon))) return null;
  const label = trim(feature?.text || feature?.place_name || 'Resolved place');
  const addressText = trim(feature?.place_name || label);
  if (!addressText) return null;
  return {
    id: `mapbox:${feature.id || addressText}`,
    source: 'mapbox',
    name: label,
    label,
    addressText,
    addressStatus: 'candidate',
    placeKind: 'geocoder_candidate',
    privacyLevel: 'operator_confirmed',
    coordinates: {
      lat: roundCoord(lat),
      lon: roundCoord(lon),
    },
    requiresConfirmation: true,
    canQuote: true,
  };
};

const isBusinessLikeQuery = (query, regionSlug = 'camden') =>
  !hasStreetNumber(query) &&
  !hasStreetTerm(query) &&
  queryCoreWords(query, regionSlug).length > 0;

const candidateTextMatchesBusinessQuery = (values, query, regionSlug = 'camden') => {
  if (!isBusinessLikeQuery(query, regionSlug)) return true;
  const coreWords = queryCoreWords(query, regionSlug);
  if (!coreWords.length) return true;
  const featureWords = normalizeWords(values.join(' '));
  const featureText = featureWords.join(' ');
  const featureCompact = featureWords.join('');
  return coreWords.every(
    (word) => featureText.includes(word) || featureCompact.includes(word),
  );
};

const featureMatchesBusinessQuery = (feature, query, regionSlug = 'camden') => {
  if (!isBusinessLikeQuery(query, regionSlug)) return true;
  const placeTypes = Array.isArray(feature?.place_type) ? feature.place_type : [];
  if (!placeTypes.includes('poi') && hasStreetTerm(feature?.text || '')) {
    return false;
  }
  return candidateTextMatchesBusinessQuery(
    [
      feature?.text,
      feature?.place_name,
      feature?.properties?.category,
      feature?.properties?.maki,
      ...(feature?.place_type || []),
    ],
    query,
    regionSlug,
  );
};

const dedupeCandidates = (candidates) => {
  const seen = new Set();
  return candidates.filter((candidate) => {
    const key = trim(
      candidate.addressText || candidate.label || candidate.name,
    ).toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const rankCandidates = (candidates) =>
  candidates.sort((a, b) => {
    if (Boolean(a.canQuote) !== Boolean(b.canQuote)) {
      return a.canQuote ? -1 : 1;
    }
    if (a.source === 'mapbox' && b.source !== 'mapbox') return -1;
    if (a.source !== 'mapbox' && b.source === 'mapbox') return 1;
    return 0;
  });

const uniqueQueries = (queries) => {
  const seen = new Set();
  return queries
    .map(trim)
    .filter(Boolean)
    .filter((query) => {
      const key = query.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const buildBusinessLookupQueries = (query, regionSlug = 'camden') => {
  const context = getRegionSearchContext(regionSlug);
  const businessTerms = queryCoreWords(query, regionSlug).join(' ');
  const zipContext = extractZipCodes(query)[0] || context.postalCode || '';
  const city = context.city || '';
  const state = context.state || '';
  const label = context.label || [city, state].filter(Boolean).join(' ');

  return uniqueQueries([
    city && zipContext ? `${businessTerms} ${city} ${zipContext}` : '',
    city && state ? `${businessTerms} ${city} ${state}` : '',
    label ? `${businessTerms} ${label}` : '',
    zipContext ? `${businessTerms} ${zipContext}` : '',
    query,
    businessTerms,
  ]);
};

const createSearchSessionToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `jg-localops-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const normalizeSearchBoxFeature = (feature, suggestion) => {
  const coords = feature?.geometry?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const [lon, lat] = coords;
  if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lon))) return null;
  const props = feature?.properties || {};
  const label = trim(props.name || suggestion?.name || props.address || 'Resolved place');
  const addressText = trim(
    props.full_address ||
      suggestion?.full_address ||
      [label, props.place_formatted || suggestion?.place_formatted]
        .filter(Boolean)
        .join(', '),
  );
  if (!addressText) return null;
  return {
    id: `mapbox-search:${props.mapbox_id || suggestion?.mapbox_id || addressText}`,
    source: 'mapbox',
    name: label,
    label,
    addressText,
    addressStatus: 'candidate',
    placeKind: props.feature_type || 'geocoder_candidate',
    privacyLevel: 'operator_confirmed',
    coordinates: {
      lat: roundCoord(lat),
      lon: roundCoord(lon),
    },
    requiresConfirmation: true,
    canQuote: true,
  };
};

const shouldRetrieveSearchBoxSuggestion = (suggestion, sourceQuery, regionSlug) => {
  if (!suggestion?.mapbox_id || !trim(suggestion.full_address)) return false;
  return candidateTextMatchesBusinessQuery(
    [
      suggestion.name,
      suggestion.full_address,
      suggestion.place_formatted,
      suggestion.feature_type,
      suggestion.poi_category,
      ...(suggestion.poi_category_ids || []),
    ],
    sourceQuery,
    regionSlug,
  );
};

const fetchSearchBoxCandidates = async ({
  searchQuery,
  sourceQuery,
  token,
  limit,
  regionSlug,
  sessionToken,
}) => {
  const encoded = encodeURIComponent(searchQuery);
  const context = getRegionSearchContext(regionSlug);
  const params = new URLSearchParams({
    access_token: token,
    session_token: sessionToken,
    limit: String(Math.max(limit * 2, 8)),
    language: 'en',
    country: 'US',
  });
  const proximity = context.proximity;
  if (proximity) params.set('proximity', proximity);
  const bbox = boundsToBbox(REGION_BOUNDS[regionSlug]);
  if (bbox) params.set('bbox', bbox);
  const suggestUrl = `https://api.mapbox.com/search/searchbox/v1/suggest?q=${encoded}&${params.toString()}`;
  const response = await fetch(suggestUrl);
  if (!response.ok) throw new Error('Place search unavailable');
  const data = await response.json();
  const suggestions = (data?.suggestions || [])
    .filter((suggestion) =>
      shouldRetrieveSearchBoxSuggestion(suggestion, sourceQuery, regionSlug),
    )
    .slice(0, limit);

  const candidates = [];
  for (const suggestion of suggestions) {
    const retrieveUrl = `https://api.mapbox.com/search/searchbox/v1/retrieve/${encodeURIComponent(
      suggestion.mapbox_id,
    )}?${new URLSearchParams({
      access_token: token,
      session_token: sessionToken,
    }).toString()}`;
    const retrieveResponse = await fetch(retrieveUrl);
    if (!retrieveResponse.ok) continue;
    const retrieveData = await retrieveResponse.json();
    for (const feature of retrieveData?.features || []) {
      const candidate = normalizeSearchBoxFeature(feature, suggestion);
      if (
        candidate &&
        coordinatesInRegion(candidate.coordinates, regionSlug) &&
        candidateTextMatchesBusinessQuery(
          [candidate.name, candidate.label, candidate.addressText, candidate.placeKind],
          sourceQuery,
          regionSlug,
        )
      ) {
        candidates.push(candidate);
      }
    }
  }
  return candidates;
};

const fetchMapboxCandidates = async ({
  mapboxQuery,
  sourceQuery,
  token,
  limit,
  regionSlug,
}) => {
  const encoded = encodeURIComponent(mapboxQuery);
  const params = new URLSearchParams({
    access_token: token,
    limit: String(limit),
    language: 'en',
    country: 'us',
  });
  const proximity = getRegionSearchContext(regionSlug).proximity;
  if (proximity) params.set('proximity', proximity);
  const bbox = boundsToBbox(REGION_BOUNDS[regionSlug]);
  if (bbox) params.set('bbox', bbox);
  params.set('types', 'address,poi');
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?${params.toString()}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error('Place resolution unavailable');
  const data = await response.json();
  return (data?.features || [])
    .filter((feature) => featureMatchesBusinessQuery(feature, sourceQuery, regionSlug))
    .map(normalizeMapboxCandidate)
    .filter((candidate) =>
      Boolean(candidate) && coordinatesInRegion(candidate.coordinates, regionSlug),
    );
};

export const getConfirmedSeededCandidate = (value, regionSlug = 'camden') => {
  const text = trim(value).toLowerCase();
  if (!text) return null;
  const exact = LOCAL_PLACE_SUGGESTIONS.find((place) => {
    if (place.regionSlug !== regionSlug) return false;
    const values = [place.name, place.publicAddress, ...(place.aliases || [])]
      .map((item) => trim(item).toLowerCase())
      .filter(Boolean);
    return values.some((item) => item === text);
  });
  if (!exact) return null;
  const candidate = normalizeSeededCandidate(exact);
  return candidate.canQuote ? candidate : null;
};

export const resolveConciergePlaceCandidates = async (
  value,
  { regionSlug = 'camden', limit = 5 } = {},
) => {
  const query = trim(value);
  if (query.length < 3) return [];

  const seeded = getPlaceSuggestions(query, regionSlug).map(normalizeSeededCandidate);
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!token) return dedupeCandidates(seeded);

  try {
    const businessQuery = isBusinessLikeQuery(query, regionSlug);
    const lookupQueries = businessQuery
      ? buildBusinessLookupQueries(query, regionSlug)
      : [query];
    let resolvedCandidates = [];
    const sessionToken = createSearchSessionToken();

    if (businessQuery) {
      for (const searchQuery of lookupQueries) {
        let candidates = [];
        try {
          candidates = await fetchSearchBoxCandidates({
            searchQuery,
            sourceQuery: query,
            token,
            limit,
            regionSlug,
            sessionToken,
          });
        } catch (err) {
          break;
        }
        resolvedCandidates = dedupeCandidates([
          ...resolvedCandidates,
          ...candidates,
        ]);
        if (resolvedCandidates.some((candidate) => candidate.canQuote)) break;
      }
    }

    if (!resolvedCandidates.some((candidate) => candidate.canQuote)) {
      for (const mapboxQuery of lookupQueries) {
        const candidates = await fetchMapboxCandidates({
          mapboxQuery,
          sourceQuery: query,
          token,
          limit,
          regionSlug,
        });
        resolvedCandidates = dedupeCandidates([...resolvedCandidates, ...candidates]);
        if (resolvedCandidates.some((candidate) => candidate.canQuote)) break;
      }
    }
    return rankCandidates(dedupeCandidates([...seeded, ...resolvedCandidates])).slice(0, limit);
  } catch (err) {
    return dedupeCandidates(seeded);
  }
};

export const calculateConciergeSuggestedPayUsd = (
  distanceMiles,
  pricingRule = {},
) => {
  const miles = Number(distanceMiles);
  const rate = Number(pricingRule.rate || 0);
  if (!Number.isFinite(miles) || miles < 0 || !Number.isFinite(rate) || rate <= 0) {
    return null;
  }
  return Math.ceil(miles * rate);
};

export const calculateConciergeRouteQuote = ({
  origin,
  destination,
  pricingRule,
} = {}) => {
  if (pricingRule?.type !== 'per_mile') return null;
  const originCoords = placeCoordinates(origin);
  const destinationCoords = placeCoordinates(destination);
  if (!originCoords || !destinationCoords) return null;
  const distanceMeters = getDistanceFromLatLonInMeters(
    originCoords.lat,
    originCoords.lon,
    destinationCoords.lat,
    destinationCoords.lon,
  );
  const distanceMiles = distanceMeters / 1609.344;
  const suggestedPayUsd = calculateConciergeSuggestedPayUsd(
    distanceMiles,
    pricingRule,
  );
  if (!Number.isFinite(suggestedPayUsd)) return null;
  return {
    status: 'quoted',
    originLabel: origin.label || origin.name || origin.addressText,
    destinationLabel:
      destination.label || destination.name || destination.addressText,
    distanceMiles,
    suggestedPayUsd,
    pricingRule,
  };
};
